import React from 'react';
import { useTranslation } from "react-i18next";
import { ENTERPRISE_PORTAL_URL } from "../../../constants";
import { Link } from 'react-router-dom';

function HeroSection({
  heading,
  subHeading,
  actionBtnName,
  primaryColor
}) {
  const { t } = useTranslation("global");
  
  return (
    <section id="hero" className="py-12 bg-gradient-to-b from-gray-50 to-white overflow-hidden">
      <div className="container relative mx-auto flex flex-col gap-8 p-4 md:flex-row md:justify-between md:items-center">
        <div className="space-y-6 flex flex-col items-center md:items-start md:w-1/2">
          <div className="text-3xl md:text-4xl lg:text-5xl text-black font-heading font-semibold text-center md:text-left">
            {heading}
          </div>
          
          <div className="text-lg md:text-xl text-gray-600 font-text text-center md:text-left max-w-xl">
            {subHeading.split('\n').map((paragraph, index) => (
              <React.Fragment key={index}>
                {index > 0 && <br />}
                {paragraph}
              </React.Fragment>
            ))}
          </div>
          
          {/* Centered buttons on mobile with flex-col on small screens and flex-row on larger screens */}
          <div className="flex flex-col sm:flex-row items-center justify-center md:justify-start w-full gap-4">
            <a
              href={`${ENTERPRISE_PORTAL_URL}/sign-up`}
              className={`bg-${primaryColor} py-3 px-8 text-base hover:shadow-lg font-text rounded-full text-white font-semibold transition-all hover:scale-105 text-center`}
            >
              {actionBtnName}
            </a>
            
            <Link
              id="demo-tour"
              to="demo"
              className="font-semibold bg-black text-base flex items-center justify-center py-3 px-8 font-text rounded-full text-white transition-all hover:scale-105"
            >
              {t("header.demo")}
            </Link>
          </div>
        </div>
        
        <div className="md:w-1/2 mt-8 md:mt-0 relative perspective-1000">
          {/* 3D Glass Card Bank */}
          <div className="grid grid-cols-2 gap-6 transform rotate-6">
            {/* SSI Card - Added text-center to mobile spans */}
            <div className="transform transition-all duration-300 hover:-translate-y-2 hover:rotate-3 group">
              <div className="relative h-44 w-full rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-600/20 backdrop-blur-md border border-white/20 shadow-xl flex flex-col items-center justify-center overflow-hidden">
                <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-blue-500/30 blur-xl"></div>
                <div className="absolute -bottom-8 -left-8 w-28 h-28 rounded-full bg-indigo-600/20 blur-xl"></div>
                
                <div className="w-16 h-16 mb-2 flex items-center justify-center bg-blue-600/90 text-white rounded-full shadow-lg group-hover:scale-110 transition-transform">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <span className="text-xl font-bold text-blue-900 mt-2 z-10 text-center w-full">{t("home.1", "SSI")}</span>
                <span className="text-sm text-blue-800/70 z-10 text-center w-full">{t("home.ssi-full", "Sécurité de l'information")}</span>
              </div>
            </div>
            
            {/* QSE Card - Changed to amber/orange */}
            <div className="transform transition-all duration-300 hover:-translate-y-2 hover:rotate-3 group">
              <div className="relative h-44 w-full rounded-xl bg-gradient-to-br from-amber-500/20 to-amber-600/20 backdrop-blur-md border border-white/20 shadow-xl flex flex-col items-center justify-center overflow-hidden">
                <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-amber-500/30 blur-xl"></div>
                <div className="absolute -bottom-8 -left-8 w-28 h-28 rounded-full bg-orange-600/20 blur-xl"></div>
                
                <div className="w-16 h-16 mb-2 flex items-center justify-center bg-amber-600/90 text-white rounded-full shadow-lg group-hover:scale-110 transition-transform">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <span className="text-xl font-bold text-amber-900 mt-2 z-10 text-center w-full">{t("home.2", "QSE")}</span>
                <span className="text-sm text-amber-800/70 z-10 text-center w-full">{t("home.qse-full", "Qualité, Sécurité, Environnement")}</span>
              </div>
            </div>
            
            {/* RSE Card - Changed to green */}
            <div className="transform transition-all duration-300 hover:-translate-y-2 hover:rotate-3 group">
              <div className="relative h-44 w-full rounded-xl bg-gradient-to-br from-green-500/20 to-green-600/20 backdrop-blur-md border border-white/20 shadow-xl flex flex-col items-center justify-center overflow-hidden">
                <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-green-500/30 blur-xl"></div>
                <div className="absolute -bottom-8 -left-8 w-28 h-28 rounded-full bg-teal-600/20 blur-xl"></div>
                
                <div className="w-16 h-16 mb-2 flex items-center justify-center bg-green-600/90 text-white rounded-full shadow-lg group-hover:scale-110 transition-transform">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                  </svg>
                </div>
                <span className="text-xl font-bold text-green-900 mt-2 z-10 text-center w-full">{t("home.3", "RSE")}</span>
                <span className="text-sm text-green-800/70 z-10 text-center w-full">{t("home.rse-full", "Responsabilité Sociétale")}</span>
              </div>
            </div>
            
            {/* RGPD Card - Now using purple */}
            <div className="transform transition-all duration-300 hover:-translate-y-2 hover:rotate-3 group">
              <div className="relative h-44 w-full rounded-xl bg-gradient-to-br from-purple-500/20 to-purple-600/20 backdrop-blur-md border border-white/20 shadow-xl flex flex-col items-center justify-center overflow-hidden">
                <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-purple-500/30 blur-xl"></div>
                <div className="absolute -bottom-8 -left-8 w-28 h-28 rounded-full bg-fuchsia-600/20 blur-xl"></div>
                
                <div className="w-16 h-16 mb-2 flex items-center justify-center bg-purple-600/90 text-white rounded-full shadow-lg group-hover:scale-110 transition-transform">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                  </svg>
                </div>
                <span className="text-xl font-bold text-purple-900 mt-2 z-10 text-center w-full">{t("home.rgpd", "RGPD")}</span>
                <span className="text-sm text-purple-800/70 z-10 text-center w-full">{t("home.rgpd-full", "Protection des Données")}</span>
              </div>
            </div>
          </div>
          
          {/* Background decorative elements */}
          <div className="absolute w-40 h-40 rounded-full bg-purple-100 bottom-10 -left-5 opacity-30 blur-lg"></div>
        </div>
      </div>
    </section>
  );
}

export default HeroSection;